import { Component, ChangeDetectorRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { CadastroService } from '../../../services/cadastro.service';
import { FinanceiroService } from '../../../services/financeiro.service';
import { ParametrosService } from '../../../services/parametros.service';
import { Cliente, ClienteSimplificado, Empresa, VendedorCadastro } from '../../../models/cadastro.model';
import { OrcamentoDTO } from '../../../models/financeiro.model';
import { ResumoVendaV2, TIPO_VENDA_A_VISTA, TIPO_VENDA_PARCELADA, TIPO_VENDA_MISTA } from '../../../models/venda-v2.model';
import { ErrorModel } from '../../../models/error.model';
import { todayLocalDateString } from '../../../core/utils/date.util';
import { formatarMoedaBRL } from '../../../core/utils/moeda.util';
import { formatarCnpj, formatarTelefone, montarEnderecoEmpresa } from '../../../core/utils/empresa.util';
import { VendaModalV2 } from '../venda-modal-v2/venda-modal-v2';
import { ClienteModal } from '../cliente-modal/cliente-modal';

const OBSERVACAO_MIN_LENGTH = 15;

const LABELS_FORMA: Record<string, string> = {
  'CREDITO A VISTA':   'Crédito à Vista',
  'CREDITO PARCELADO': 'Crédito Parcelado',
  'DEBITO':            'Débito',
  'ESPECIE':           'Espécie',
  'PIX':               'Pix',
  'BOLETO':            'Boleto',
  'CHEQUE':            'Cheque',
  'VENDA MISTA':       'Venda Mista'
};

const LABELS_TIPO_VENDA: Record<string, string> = {
  [TIPO_VENDA_A_VISTA]:   'À Vista',
  [TIPO_VENDA_PARCELADA]: 'Parcelada',
  [TIPO_VENDA_MISTA]:     'Venda Mista'
};

export interface CupomVendaBalcao {
  numeroReferencia: number;
  emitidoEm: Date;
  nomeVendedor: string;
  nomeCliente: string;
  cpfCnpjCliente: string;
  subtotal: number;
  desconto: number;
  valorTotal: number;
  observacao: string;
  tipoVenda: string;
  formaPagamento: string;
  bandeira: string;
  numParcelas: number;
  valorEntrada: number | null;
  previsaoEntrega: string;
  empresa: Empresa | null;
}

// Valida sobre o texto sem espaços nas bordas, para impedir observações só de espaços.
function observacaoObrigatoriaValidator(control: AbstractControl): ValidationErrors | null {
  const valor = String(control.value ?? '').trim();
  if (!valor) return { required: true };
  if (valor.length < OBSERVACAO_MIN_LENGTH) {
    return { minlength: { requiredLength: OBSERVACAO_MIN_LENGTH, actualLength: valor.length } };
  }
  return null;
}

@Component({
  selector: 'app-venda-balcao-cupom-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, VendaModalV2, ClienteModal],
  templateUrl: './venda-balcao-cupom-modal.html',
  styleUrl: './venda-balcao-cupom-modal.css'
})
export class VendaBalcaoCupomModal implements OnChanges {
  @Input() isOpen = false;
  @Output() closeModal = new EventEmitter<boolean>();

  form: FormGroup;
  isLoading = false;
  isClienteModalOpen = false;
  isVendaModalOpen = false;
  isCupomOpen = false;
  cupom: CupomVendaBalcao | null = null;
  empresa: Empresa | null = null;

  readonly formatarCnpj = formatarCnpj;
  readonly formatarTelefone = formatarTelefone;
  readonly montarEnderecoEmpresa = montarEnderecoEmpresa;

  nomeVendedor = '';
  nomeCliente = '';
  cpfCnpjCliente = '';
  private resumoVenda: ResumoVendaV2 | null = null;
  orcamentoCriado: OrcamentoDTO | null = null;
  /** Restrição do cliente selecionado — usado para bloquear PARCELADA/VENDA MISTA no venda-modal-v2 (D-11). */
  clienteRestricaoAtiva = false;

  limiteMinimo: number | null = null;
  limiteMaximo: number | null = null;
  limitesCarregados = false;

  private fb = inject(FormBuilder);
  private toastr = inject(ToastrService);
  private cadastroService = inject(CadastroService);
  private financeiroService = inject(FinanceiroService);
  private parametrosService = inject(ParametrosService);
  private cdr = inject(ChangeDetectorRef);

  constructor() {
    this.form = this.fb.group({
      codigoVendedor: [null, [Validators.required, Validators.min(1)]],
      idVendedor: [null, [Validators.required, Validators.min(1)]],
      codigoCliente: [null, [Validators.required, Validators.min(1)]],
      idCliente: [null, [Validators.required, Validators.min(1)]],
      subtotal: [null, [Validators.required, Validators.min(0.01)]],
      descontoAplicado: [0, [Validators.required, Validators.min(0)]],
      observacao: ['', [observacaoObrigatoriaValidator]]
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue === true) {
      this.resetState();
      this.carregarLimites();
      this.carregarEmpresa();
    }
  }

  private carregarEmpresa(): void {
    this.empresa = null;
    this.cadastroService.getEmpresas().subscribe({
      next: (empresas) => {
        this.empresa = (empresas ?? []).find(e => e.status === true) ?? null;
        this.cdr.detectChanges();
      },
      error: () => {
        this.empresa = null;
        this.cdr.detectChanges();
      }
    });
  }

  private carregarLimites(): void {
    this.parametrosService.getParametrosByCategorias(['VENDA_BALCAO_LIMITE']).subscribe({
      next: (parametros) => {
        const limite = parametros.find(p => p.categoria === 'VENDA_BALCAO_LIMITE');
        this.limiteMinimo = this.parseValor(limite?.valorMinimo);
        this.limiteMaximo = this.parseValor(limite?.valorMaximo);
        this.limitesCarregados = !!limite;
        this.cdr.detectChanges();
      },
      error: () => {
        this.limiteMinimo = null;
        this.limiteMaximo = null;
        this.limitesCarregados = false;
        this.cdr.detectChanges();
      }
    });
  }

  private parseValor(valor: string | undefined): number | null {
    if (valor === undefined || valor === null || String(valor).trim() === '') return null;
    const numero = Number(valor);
    return Number.isFinite(numero) ? numero : null;
  }

  private formatBRL(valor: number): string {
    return formatarMoedaBRL(valor);
  }

  private resetState(): void {
    this.form.reset({ descontoAplicado: 0, observacao: '' });
    this.nomeVendedor = '';
    this.nomeCliente = '';
    this.cpfCnpjCliente = '';
    this.resumoVenda = null;
    this.cupom = null;
    this.isCupomOpen = false;
    this.orcamentoCriado = null;
    this.clienteRestricaoAtiva = false;
    this.isLoading = false;
    this.isClienteModalOpen = false;
    this.isVendaModalOpen = false;
  }

  get valorTotal(): number {
    const subtotal = this.form.get('subtotal')?.value ?? 0;
    const desconto = this.form.get('descontoAplicado')?.value ?? 0;
    return Math.max(0, subtotal - desconto);
  }

  get descontoInvalido(): boolean {
    const subtotal = this.form.get('subtotal')?.value ?? 0;
    const desconto = this.form.get('descontoAplicado')?.value ?? 0;
    return desconto > subtotal;
  }

  buscarVendedor(): void {
    const codigo = this.form.get('codigoVendedor')?.value;
    if (!codigo || codigo <= 0) return;

    this.cadastroService.getVendedorById(+codigo).subscribe({
      next: (vendedor: VendedorCadastro) => {
        if (!vendedor.ativo) {
          this.toastr.warning(`Vendedor ${vendedor.nomeFuncionario} ${vendedor.sobrenomeFuncionario} está inativo.`);
          this.form.patchValue({ idVendedor: null });
          this.nomeVendedor = '';
          this.form.get('codigoVendedor')?.setErrors({ inativo: true });
          this.cdr.detectChanges();
          return;
        }
        this.nomeVendedor = `${vendedor.nomeFuncionario} ${vendedor.sobrenomeFuncionario}`.trim();
        this.form.patchValue({ idVendedor: vendedor.id });
        this.form.get('codigoVendedor')?.setErrors(null);
        this.cdr.detectChanges();
      },
      error: () => {
        this.toastr.error('Vendedor não encontrado. Verifique o código informado.');
        this.nomeVendedor = '';
        this.form.patchValue({ idVendedor: null });
        this.form.get('codigoVendedor')?.setErrors({ notFound: true });
        this.cdr.detectChanges();
      }
    });
  }

  buscarCliente(): void {
    const codigo = this.form.get('codigoCliente')?.value;
    if (!codigo || codigo <= 0) return;

    this.cadastroService.getClienteById(+codigo).subscribe({
      next: (cliente: Cliente) => {
        this.nomeCliente = cliente.nomeRazaoSocial;
        this.cpfCnpjCliente = cliente.cpfCnpj ?? '';
        this.clienteRestricaoAtiva = !!cliente.restricaoAtiva;
        this.form.patchValue({ idCliente: cliente.id });
        this.form.get('codigoCliente')?.setErrors(null);
        this.cdr.detectChanges();
      },
      error: () => {
        this.toastr.error('Cliente não encontrado. Verifique o código informado.');
        this.nomeCliente = '';
        this.cpfCnpjCliente = '';
        this.form.patchValue({ idCliente: null });
        this.form.get('codigoCliente')?.setErrors({ notFound: true });
        this.cdr.detectChanges();
      }
    });
  }

  abrirClienteModal(): void {
    this.isClienteModalOpen = true;
    this.cdr.detectChanges();
  }

  closeClienteModal(cliente?: ClienteSimplificado): void {
    this.isClienteModalOpen = false;
    if (cliente && cliente.id > 0) {
      this.nomeCliente = cliente.nomeRazaoSocial;
      this.form.patchValue({
        codigoCliente: cliente.id,
        idCliente: cliente.id
      });
      this.form.get('codigoCliente')?.setErrors(null);
      // ClienteSimplificado não traz restricaoAtiva — busca o cliente completo (D-11).
      this.cadastroService.getClienteById(cliente.id).subscribe(c => {
        this.clienteRestricaoAtiva = !!c.restricaoAtiva;
        this.cpfCnpjCliente = c.cpfCnpj ?? '';
      });
    }
    this.cdr.detectChanges();
  }

  salvar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastr.warning('Preencha todos os campos obrigatórios.');
      return;
    }

    if (this.descontoInvalido) {
      this.toastr.warning('O desconto não pode ser maior que o subtotal.');
      return;
    }

    if (this.valorTotal <= 0) {
      this.toastr.warning('O valor total deve ser maior que zero.');
      return;
    }

    if (!this.limitesCarregados) {
      this.toastr.error('Não foi possível validar os limites da venda balcão. Tente novamente.');
      this.carregarLimites();
      return;
    }

    if (this.limiteMaximo !== null && this.valorTotal > this.limiteMaximo) {
      this.toastr.warning(
        `O valor total excede o limite de ${this.formatBRL(this.limiteMaximo)} para venda balcão. Esta venda deverá ser feita a partir de um orçamento.`
      );
      return;
    }

    if (this.limiteMinimo !== null && this.valorTotal < this.limiteMinimo) {
      this.toastr.warning(
        `O valor total é inferior ao mínimo de ${this.formatBRL(this.limiteMinimo)} permitido para venda balcão.`
      );
      return;
    }

    const hoje = todayLocalDateString();
    const idVendedor = this.form.get('idVendedor')?.value;
    const idCliente = this.form.get('idCliente')?.value;
    const subtotal = this.form.get('subtotal')?.value;
    const descontoAplicado = this.form.get('descontoAplicado')?.value ?? 0;

    const payload = {
      id: null,
      vendedor: { id: idVendedor },
      cliente: { id: idCliente },
      statusOrcamento: 'ABERTO',
      descontoAplicado: descontoAplicado,
      dataOrcamento: hoje,
      validadeOrcamento: hoje,
      observacao: String(this.form.get('observacao')?.value ?? '').trim(),
      dataAtualizacao: hoje,
      subtotal: subtotal,
      valorTotal: this.valorTotal,
      custoInstalacao: 0
    };

    this.isLoading = true;
    this.financeiroService.createOrcamento(payload as any).subscribe({
      next: (orcamento: any) => {
        this.isLoading = false;
        // Monta o OrcamentoDTO mínimo exigido pelo VendaModalV2
        this.orcamentoCriado = {
          id: orcamento.id,
          idCliente: idCliente,
          idVendedor: idVendedor,
          nomeCliente: this.nomeCliente,
          descontoAplicado: descontoAplicado,
          valorTotal: this.valorTotal,
          dataOrcamento: hoje,
          validadeOrcamento: hoje,
          diasEmAberto: 0,
          restricaoAtiva: this.clienteRestricaoAtiva
        };
        this.isVendaModalOpen = true;
        this.cdr.detectChanges();
      },
      error: (err) => {
        const erro: ErrorModel = err.error ?? { message: 'Erro ao criar orçamento.' };
        this.toastr.error(erro.message ?? 'Erro ao criar orçamento.');
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  onVendaEfetuada(resumo: ResumoVendaV2): void {
    this.resumoVenda = resumo;
  }

  closeVendaModal(concluida: boolean): void {
    const orcamento = this.orcamentoCriado;
    this.isVendaModalOpen = false;
    this.orcamentoCriado = null;

    if (!concluida || !orcamento) {
      // Orçamento já foi criado, mas a venda não: devolve false e deixa a tela como está.
      this.cdr.detectChanges();
      return;
    }

    const resumo = this.resumoVenda;
    const subtotal = this.form.get('subtotal')?.value ?? 0;
    const forma = resumo?.formaPagamento ?? '';
    this.cupom = {
      numeroReferencia: orcamento.id,
      emitidoEm: new Date(),
      nomeVendedor: this.nomeVendedor,
      nomeCliente: this.nomeCliente,
      cpfCnpjCliente: this.cpfCnpjCliente,
      subtotal,
      desconto: orcamento.descontoAplicado,
      valorTotal: orcamento.valorTotal,
      observacao: String(this.form.get('observacao')?.value ?? '').trim(),
      tipoVenda: LABELS_TIPO_VENDA[resumo?.tipoVendaPagamento ?? ''] ?? (resumo?.tipoVendaPagamento ?? ''),
      formaPagamento: LABELS_FORMA[forma] ?? forma,
      bandeira: resumo?.bandeiraCartao ?? '',
      numParcelas: resumo?.numParcelas ?? 1,
      valorEntrada: resumo?.valorEntrada ?? null,
      previsaoEntrega: resumo?.previsaoEntrega ?? '',
      empresa: this.empresa
    };
    this.isCupomOpen = true;
    this.cdr.detectChanges();
  }

  fecharCupom(): void {
    this.isCupomOpen = false;
    this.cupom = null;
    this.closeModal.emit(true);
  }

  imprimirCupom(): void {
    const c = this.cupom;
    if (!c) return;

    const esc = (v: unknown): string =>
      String(v ?? '').replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] as string));
    const linha = (rotulo: string, valor: string) =>
      `<div class="linha"><span>${esc(rotulo)}</span><span>${esc(valor)}</span></div>`;

    const emp = c.empresa;
    const empLinha = (v: string | null | undefined, extra = ''): string =>
      v && v.trim() ? `<div${extra ? ` class="${extra}"` : ''}>${esc(v)}</div>` : '';
    const empresaHtml = emp ? `<div class="empresa centro">
    ${empLinha(emp.nomeFantasia, 'empresa-fantasia')}
    ${empLinha(emp.razaoSocial)}
    ${empLinha(emp.cnpj?.trim() ? 'CNPJ ' + formatarCnpj(emp.cnpj) : '')}
    ${empLinha(emp.inscricaoEstadual?.trim() ? 'IE ' + emp.inscricaoEstadual.trim() : '')}
    ${empLinha(montarEnderecoEmpresa(emp))}
    ${empLinha(emp.telefone ? formatarTelefone(emp.telefone) : '')}
    ${empLinha(emp.email)}
  </div>` : '';

    const pagamento = [
      linha('Tipo', c.tipoVenda),
      linha('Forma', c.formaPagamento),
      c.bandeira ? linha('Bandeira', c.bandeira) : '',
      c.numParcelas > 1 ? linha('Parcelas', `${c.numParcelas}x`) : '',
      c.valorEntrada ? linha('Entrada', formatarMoedaBRL(c.valorEntrada)) : '',
      c.previsaoEntrega ? linha('Previsão entrega', this.formatarData(c.previsaoEntrega)) : ''
    ].join('');

    const html = `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="utf-8"><title>Cupom não fiscal #${esc(c.numeroReferencia)}</title>
<style>
  @page { size: 80mm auto; margin: 4mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Courier New', monospace; font-size: 12px; width: 72mm; margin: 0 auto; color: #000; }
  .centro { text-align: center; }
  .aviso { border: 2px solid #000; padding: 4px; margin: 6px 0; font-weight: bold; text-align: center; }
  .sep { border-top: 1px dashed #000; margin: 6px 0; }
  .linha { display: flex; justify-content: space-between; gap: 8px; }
  .total { font-size: 15px; font-weight: bold; }
  .obs { white-space: pre-wrap; word-break: break-word; }
  .empresa { margin: 6px 0; word-break: break-word; overflow-wrap: anywhere; }
  .empresa-fantasia { font-weight: bold; font-size: 14px; }
  img { max-width: 40mm; }
</style></head><body>
  <div class="centro"><img src="${window.location.origin}/assets/logo/logo_transparente.png" alt=""></div>
  ${empresaHtml}
  <div class="aviso">DOCUMENTO NÃO FISCAL<br>SEM VALOR FISCAL</div>
  <div class="centro">Comprovante de Venda Balcão</div>
  <div class="sep"></div>
  ${linha('Referência', '#' + c.numeroReferencia)}
  ${linha('Emissão', this.formatarDataHora(c.emitidoEm))}
  ${linha('Vendedor', c.nomeVendedor)}
  <div class="sep"></div>
  <div>Cliente: ${esc(c.nomeCliente)}</div>
  ${c.cpfCnpjCliente ? `<div>CPF/CNPJ: ${esc(c.cpfCnpjCliente)}</div>` : ''}
  <div class="sep"></div>
  ${linha('Subtotal', formatarMoedaBRL(c.subtotal))}
  ${linha('Desconto', '- ' + formatarMoedaBRL(c.desconto))}
  <div class="linha total"><span>TOTAL</span><span>${esc(formatarMoedaBRL(c.valorTotal))}</span></div>
  <div class="sep"></div>
  ${pagamento}
  <div class="sep"></div>
  <div>Observação:</div>
  <div class="obs">${esc(c.observacao)}</div>
  <div class="sep"></div>
  <div class="aviso">ESTE DOCUMENTO NÃO É UM CUPOM FISCAL</div>
</body></html>`;

    const janela = window.open('', '_blank');
    if (!janela) {
      this.toastr.warning('Não foi possível abrir a janela de impressão. Verifique o bloqueador de pop-ups.');
      return;
    }
    janela.document.write(html);
    janela.document.close();
    janela.focus();
    janela.onload = () => janela.print();
    // Fallback caso o onload já tenha disparado antes do handler ser atribuído.
    setTimeout(() => janela.print(), 500);
  }

  formatarData(iso: string): string {
    const [ano, mes, dia] = iso.split('-');
    return ano && mes && dia ? `${dia}/${mes}/${ano}` : iso;
  }

  formatarDataHora(d: Date): string {
    return d.toLocaleString('pt-BR');
  }

  onClose(): void {
    if (this.isLoading) return;
    this.closeModal.emit(false);
  }
}
